import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { LoadingOverlay } from "../loadingOverlay/LoadingOverlay";
import { ProgressSpinner } from "../progressSpinner/ProgressSpinner";
import {
  cell,
  container,
  emptyState,
  loadingRow,
  loadingState,
  row,
  totalSizeWrapper
} from "./CardGrid.css";
import {
  chunkIntoRows,
  computeColumns,
  findScrollParent
} from "./computeColumns";

export interface CardGridPagination {
  /** Whether more items can be loaded. */
  hasNextPage: boolean;
  /** Whether the next page is currently loading. */
  isFetchingNextPage: boolean;
  /** Loads the next page of items. */
  fetchNextPage: () => void;
}

export interface CardGridProps<T> extends React.HTMLAttributes<HTMLDivElement> {
  /** Items to render in the grid. */
  items: T[];
  /** Unique key for each item. */
  getKey: (item: T) => string;
  /** Render a single card. */
  renderItem: (item: T) => ReactNode;
  /** Minimum card width in pixels, used to derive columns-per-row. */
  minCardWidth?: number;
  /** Gap between cards in pixels. */
  gap?: number;
  /** Estimated row height in pixels, used to seed virtualizer sizing before rows are measured. */
  estimateRowHeight?: number;
  /** Enables infinite-scroll pagination; omit for a static, non-paginated grid. */
  pagination?: CardGridPagination;
  /**
   * Shows a loading overlay instead of the empty state — use this while
   * the initial data fetch is in flight, so an empty `items` array
   * isn't mistaken for a genuinely empty result.
   */
  isLoading?: boolean;
}

const DEFAULT_MIN_CARD_WIDTH = 320;
const DEFAULT_GAP = 16;
const DEFAULT_ESTIMATED_ROW_HEIGHT = 380;

/**
 * Virtualized, wrapping multi-column card grid.
 */
export function CardGrid<T>({
  items,
  getKey,
  renderItem,
  minCardWidth = DEFAULT_MIN_CARD_WIDTH,
  gap = DEFAULT_GAP,
  estimateRowHeight = DEFAULT_ESTIMATED_ROW_HEIGHT,
  pagination,
  isLoading,
  className,
  ...rest
}: CardGridProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const [columns, setColumns] = useState(1);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    setScrollElement(findScrollParent(element));
    setColumns(
      computeColumns(element.getBoundingClientRect().width, minCardWidth, gap)
    );

    let frame: number | null = null;
    const observer = new ResizeObserver(([entry]) => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setColumns(computeColumns(entry.contentRect.width, minCardWidth, gap));
      });
    });
    observer.observe(element);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [minCardWidth, gap]);

  const rowGroups = useMemo(
    () => chunkIntoRows(items, columns),
    [items, columns]
  );

  const virtualizer = useVirtualizer({
    count: rowGroups.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => estimateRowHeight,
    overscan: 3
  });

  const virtualRows = virtualizer.getVirtualItems();
  const lastVirtualRow = virtualRows[virtualRows.length - 1];
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = pagination ?? {};

  useEffect(() => {
    if (!lastVirtualRow) return;
    if (!hasNextPage || isFetchingNextPage) return;
    if (lastVirtualRow.index >= rowGroups.length - 1) {
      fetchNextPage?.();
    }
  }, [
    lastVirtualRow,
    rowGroups.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  ]);

  return (
    <div ref={containerRef} className={clsx(container, className)} {...rest}>
      {isLoading ? (
        <div className={loadingState}>
          <LoadingOverlay aria-label="Loading items" />
        </div>
      ) : items.length === 0 ? (
        <div className={emptyState}>No items to display.</div>
      ) : (
        <div
          className={totalSizeWrapper}
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualRows.map((virtualRow) => (
            <div
              key={virtualRow.index}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
              className={row}
              style={{
                transform: `translateY(${virtualRow.start}px)`,
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
                columnGap: gap
              }}
            >
              {rowGroups[virtualRow.index].map((item) => (
                <div
                  key={getKey(item)}
                  className={cell}
                  style={{ marginBottom: gap }}
                >
                  {renderItem(item)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {pagination?.isFetchingNextPage && (
        <div className={loadingRow}>
          <ProgressSpinner />
        </div>
      )}
    </div>
  );
}
